import { useQuery } from "@tanstack/react-query";
import { Info, Link, Trash2, UserMinus } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { ConfirmationButton } from "@/ds/ConfirmationButton";
import type { MySelf } from "@/utils";
import { CopyToClipboardButton } from "../../ds/CopyToClipboardButton";
import { canManageManagers as canManageManagersRole } from "../../permissions";
import type { AccessRole, SharingInfo } from "../../registry";
import {
  useCreateInvitation,
  useRemoveAssignment,
  useRevokeInvitation,
  useUpdateAssignmentRole,
} from "./mutations";

const ROLE_LABELS: Record<AccessRole | "owner", string> = {
  owner: "Owner",
  manager: "Manager",
  writer: "Writer",
  voice_actor: "Voice Actor",
  reader: "Reader",
};

const EXPIRY_LABELS: Record<string, string> = {
  "1d": "1 day",
  "7d": "7 days",
  "30d": "30 days",
};

interface ShareDialogProps {
  seriesPk: number;
  seriesTitle: string;
  user: MySelf;
  userRole: "owner" | "manager";
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ShareDialog({
  seriesPk,
  seriesTitle,
  user,
  userRole,
  open,
  onOpenChange,
}: ShareDialogProps) {
  const { data, isLoading, refetch } = useQuery<SharingInfo>({
    queryKey: ["sharing", seriesPk],
    queryFn: () =>
      fetch(`/api/books/books/${seriesPk}/sharing/`).then((r) => r.json()),
    enabled: open,
  });

  const removeAssignment = useRemoveAssignment(seriesPk);
  const updateRole = useUpdateAssignmentRole(seriesPk);
  const createInvitation = useCreateInvitation(seriesPk);
  const revokeInvitation = useRevokeInvitation(seriesPk);

  const [newInviteRole, setNewInviteRole] = useState<AccessRole>("writer");
  const [newInviteExpiry, setNewInviteExpiry] = useState("7d");

  const inviteUrl = (token: string) =>
    `${window.location.origin}/invite/${token}`;

  const handleCreateInvitation = () => {
    createInvitation.mutate(
      { role: newInviteRole, expiresIn: newInviteExpiry },
      { onSuccess: () => refetch() },
    );
  };

  const handleRemoveAssignment = (pk: number) => {
    removeAssignment.mutate(pk, { onSuccess: () => refetch() });
  };

  const handleUpdateRole = (pk: number, role: AccessRole) => {
    updateRole.mutate({ pk, role }, { onSuccess: () => refetch() });
  };

  const handleRevokeInvitation = (pk: number) => {
    revokeInvitation.mutate(pk, { onSuccess: () => refetch() });
  };

  const canManageManagers = canManageManagersRole(userRole);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Sharing — {seriesTitle}</DialogTitle>
        </DialogHeader>

        {user.userTier === "internal" && (
          <div className="flex items-start gap-2 rounded-md border border-info-border bg-info-surface px-3 py-2.5 text-xs text-info-text">
            <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>
              Internal users already have <strong>Manager</strong> access to all
              non-community series by default and don't need to be added
              explicitly.
            </span>
          </div>
        )}

        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : (
          <div className="flex flex-col gap-4">
            {/* Active access */}
            <section>
              <h3 className="text-sm font-semibold mb-2">Active access</h3>
              <ul className="flex flex-col gap-1">
                {/* Owner row — always shown, non-editable */}
                {data?.owner && (
                  <li className="flex items-center justify-between gap-2 text-sm">
                    <span className="font-medium truncate">
                      {data.owner.username}
                    </span>
                    <span className="text-xs text-muted-foreground px-3">
                      {ROLE_LABELS.owner}
                    </span>
                  </li>
                )}
                {data?.assignments.map((a) => (
                  <li
                    key={a.pk}
                    className="flex items-center justify-between gap-2 text-sm"
                  >
                    <span className="font-medium truncate">
                      {a.user.username}
                    </span>
                    <div className="flex items-center gap-1 shrink-0">
                      <Select
                        value={a.role}
                        onValueChange={(v) =>
                          handleUpdateRole(a.pk, v as AccessRole)
                        }
                        disabled={a.role === "manager" && !canManageManagers}
                      >
                        <SelectTrigger
                          size="sm"
                          className="w-32"
                          aria-label="Access role"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {/* Always include Manager item so the select isn't empty
                              when the assignment is already a manager */}
                          <SelectItem
                            value="manager"
                            disabled={!canManageManagers}
                            className={!canManageManagers ? "hidden" : ""}
                          >
                            {ROLE_LABELS.manager}
                          </SelectItem>
                          <SelectItem value="writer">
                            {ROLE_LABELS.writer}
                          </SelectItem>
                          <SelectItem value="voice_actor">
                            {ROLE_LABELS.voice_actor}
                          </SelectItem>
                          <SelectItem value="reader">
                            {ROLE_LABELS.reader}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <ConfirmationButton
                        variant="destructive"
                        size="icon"
                        disabled={a.role === "manager" && !canManageManagers}
                        onClick={() => handleRemoveAssignment(a.pk)}
                        aria-label={`Remove ${a.user.username}`}
                      >
                        <UserMinus className="h-4 w-4" aria-hidden="true" />
                      </ConfirmationButton>
                    </div>
                  </li>
                ))}
                {!data?.assignments.length && (
                  <li className="text-xs text-muted-foreground">
                    No collaborators added.
                  </li>
                )}
              </ul>
            </section>

            <Separator />

            {/* Invite links */}
            <section>
              <h3 className="text-sm font-semibold mb-2">Invite links</h3>
              {!data?.invitations.length ? (
                <p className="text-xs text-muted-foreground">
                  No active links.
                </p>
              ) : (
                <ul className="flex flex-col gap-1">
                  {data.invitations.map((inv) => (
                    <li
                      key={inv.pk}
                      className="flex items-center justify-between gap-2 text-sm"
                    >
                      <div className="flex items-center gap-1 min-w-0">
                        <Link className="h-3 w-3 shrink-0 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground truncate">
                          {ROLE_LABELS[inv.role]} · expires{" "}
                          {new Date(inv.expiresAt).toLocaleDateString("it-IT")}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <CopyToClipboardButton
                          variant="ghost"
                          size="icon"
                          value={inviteUrl(inv.token)}
                          aria-label="Copy invite link"
                        />
                        <ConfirmationButton
                          variant="destructive"
                          size="icon"
                          onClick={() => handleRevokeInvitation(inv.pk)}
                          aria-label="Revoke invite link"
                        >
                          <Trash2 className="h-4 w-4" aria-hidden="true" />
                        </ConfirmationButton>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <Separator />

            {/* Create invite link */}
            <section>
              <h3 className="text-sm font-semibold mb-2">Create invite link</h3>
              <div className="flex items-center gap-2">
                <Select
                  value={newInviteRole}
                  onValueChange={(v) => setNewInviteRole(v as AccessRole)}
                >
                  <SelectTrigger
                    size="sm"
                    className="w-32"
                    aria-label="Role for new invite link"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {canManageManagers && (
                      <SelectItem value="manager">
                        {ROLE_LABELS.manager}
                      </SelectItem>
                    )}
                    <SelectItem value="writer">{ROLE_LABELS.writer}</SelectItem>
                    <SelectItem value="voice_actor">
                      {ROLE_LABELS.voice_actor}
                    </SelectItem>
                    <SelectItem value="reader">{ROLE_LABELS.reader}</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={newInviteExpiry}
                  onValueChange={setNewInviteExpiry}
                >
                  <SelectTrigger
                    size="sm"
                    className="w-28"
                    aria-label="Invite link expiry"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(EXPIRY_LABELS).map(([v, label]) => (
                      <SelectItem key={v} value={v}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  onClick={handleCreateInvitation}
                  disabled={createInvitation.isPending}
                >
                  Generate link
                </Button>
              </div>
              {createInvitation.data && (
                <div className="mt-2 flex items-center gap-2 p-2 rounded bg-muted text-xs font-mono break-all">
                  <span className="flex-1">
                    {inviteUrl(createInvitation.data.token)}
                  </span>
                  <CopyToClipboardButton
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0"
                    value={inviteUrl(createInvitation.data.token)}
                    aria-label="Copy invite link"
                  />
                </div>
              )}
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
